import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardContent } from 'components/ui/card'
import { Badge } from 'components/ui/badge'
import { SearchInput } from 'components/SearchInput'
import { CompositeLocationAvatar } from 'components/CompositeLocationAvatar'
import { Compass, ClipboardCheck } from 'lucide-react'
import { cn } from 'lib/utils'
import { initializeEvaluations } from 'slices/evaluationSlice'

const STATUS_COLORS: Record<string, string> = {
	DRAFT: 'bg-gray-100 text-gray-700',
	IN_ANALYSIS: 'bg-blue-100 text-blue-700',
	UNDER_REVIEW: 'bg-amber-100 text-amber-700',
	APPROVED: 'bg-green-100 text-green-700',
	REJECTED: 'bg-red-100 text-red-700',
	ARCHIVED: 'bg-gray-200 text-gray-500',
}

interface EvaluationPickerSheetProps {
	moduleName: string
	moduleDescription?: string
	onPick: (id: string) => void
}

// Inline picker rendered by each global module page (Scoring, Financials,
// Risks, …) until the user selects an evaluation. Lists every evaluation the
// user can see, filterable by name/market/portfolio. On click → onPick(id),
// which the host page wires to a `?eval=<id>` URL param.
const EvaluationPickerSheet = ({
	moduleName,
	moduleDescription,
	onPick,
}: EvaluationPickerSheetProps) => {
	const dispatch = useDispatch()
	const [search, setSearch] = useState('')

	const { entities, ids, isInitialized } = useSelector((state: any) => state.evaluations)

	useEffect(() => {
		if (!isInitialized) {
			// @ts-ignore
			dispatch(initializeEvaluations())
		}
	}, [dispatch, isInitialized])

	const evaluations = useMemo(() => ids.map((id: string) => entities[id]).filter(Boolean), [
		ids,
		entities,
	])

	const filtered = useMemo(() => {
		if (!search.trim()) return evaluations
		const q = search.toLowerCase()
		return evaluations.filter((e: any) => {
			const fields = [e?.name, e?.marketArea, e?.portfolio?.assetName].filter(Boolean)
			return fields.some((f: string) => f.toLowerCase().includes(q))
		})
	}, [evaluations, search])

	return (
		<div className="max-w-4xl mx-auto">
			<div className="flex items-start gap-3 mb-6">
				<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] shrink-0">
					<Compass className="w-5 h-5" />
				</div>
				<div className="min-w-0">
					<h1 className="text-2xl font-bold text-gray-900 leading-tight">{moduleName}</h1>
					<p className="text-sm text-gray-500 mt-0.5">
						{moduleDescription ?? 'Pick an evaluation to continue.'}
					</p>
				</div>
			</div>

			<div className="mb-4">
				<SearchInput
					value={search}
					onChange={setSearch}
					placeholder="Search evaluations by name, market, or portfolio…"
				/>
			</div>

			{filtered.length === 0 ? (
				<Card>
					<CardContent className="py-12 text-center">
						<div className="rounded-full bg-gray-100 p-3 inline-flex mb-3">
							<ClipboardCheck className="w-6 h-6 text-gray-400" />
						</div>
						<p className="text-sm text-gray-600">
							{evaluations.length === 0
								? 'You have no evaluations yet. Create one from the Evaluations page.'
								: 'No evaluations match your search.'}
						</p>
					</CardContent>
				</Card>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
					{filtered.map((e: any) => {
						const portfolioLocations = (e?.portfolio?.locations || [])
							.map((entry: any) => entry?.location)
							.filter(Boolean)
							.map((loc: any) => ({
								imageUrl: loc?.image,
								name: loc?.assetName || '',
							}))
						return (
							<button
								key={e._id}
								type="button"
								onClick={() => onPick(e._id)}
								className="text-left transition-shadow hover:shadow-md rounded-lg"
							>
								<Card className="h-full">
									<CardContent className="p-4">
										<div className="flex items-start gap-3">
											<CompositeLocationAvatar
												locations={portfolioLocations}
												size="sm"
												className="rounded-full shrink-0"
											/>
											<div className="min-w-0 flex-1">
												<div className="flex items-center gap-2 mb-1">
													<span className="font-semibold text-sm text-gray-900 truncate flex-1">
														{e.name}
													</span>
													<Badge
														className={cn(
															'text-[10px] px-1.5 py-0 shrink-0',
															STATUS_COLORS[e.status] || 'bg-gray-100 text-gray-600',
														)}
													>
														{(e.status || '').replace(/_/g, ' ')}
													</Badge>
												</div>
												{e?.portfolio?.assetName && (
													<div className="text-xs text-gray-500 truncate">
														Portfolio: {e.portfolio.assetName}
													</div>
												)}
												{e?.marketArea && (
													<div className="text-xs text-gray-500 truncate">
														Market: {e.marketArea}
													</div>
												)}
											</div>
										</div>
									</CardContent>
								</Card>
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default EvaluationPickerSheet
